import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Box, Container, Typography, Avatar, Button, IconButton, CircularProgress, Chip, Snackbar, Alert,
  Menu, MenuItem,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockIcon from "@mui/icons-material/Block";
import { FiSend } from "react-icons/fi";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import { theme } from "../theme/colors";
import { useUser } from "../context/UserContext";
import { getUserProfile, fetchUserBadgesByUserId, followUser, unfollowUser, blockUser, unblockUser, BLOCK_CONFIRM_DESCRIPTION, getFollowBlockedErrorMessage } from "../utils/discoverApi";
import { getBadgeCountLabel, getHighestEarnedBadges } from "../utils/badgeUtils";
import { fetchPosts } from "../utils/feedApi";
import { fetchUserEvents } from "../utils/eventApi";
import PostCard from "../components/CommunityComponents/PostCard";
import AuthRequiredModal from "../components/AlertComponents/AuthRequiredModal";
import ProfileLinksModal from "../components/AlertComponents/ProfileLinksModal";
import ShareProfileModal from "../components/AlertComponents/ShareProfileModal";
import DeleteConfirmModal from "../components/AlertComponents/DeleteConfirmModal";
import LinkIcon from "@mui/icons-material/Link";
import { buildProfileShareUrl } from "../utils/profileShareUrl";
import { isPoliticianAccount } from "../utils/accountTypes";

const EVENT_HOSTING_ACCOUNT_TYPES = [
  "candidate", "state_politician", "local_politician", "federal_politician",
  "public_official_candidate", "organization", "non_profit", "municipality",
  "senator", "representative",
];

const PARTY_MAP = {
  democrat: "Democrat", republican: "Republican", independent: "Independent",
  libertarian: "Libertarian", green: "Green", other: "Other",
  prefer_not_to_say: "Prefer Not to Say",
};

const LEGACY_SOCIAL_FIELDS = [
  { key: "website_url", title: "Website" },
  { key: "twitter_url", title: "Twitter/X" },
  { key: "instagram_url", title: "Instagram" },
  { key: "facebook_url", title: "Facebook" },
];

const withHttp = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// Prefer the flexible social_links list; fall back to legacy URL fields so
// existing links still render before the backfill runs.
function getProfileSocialLinks(profile) {
  if (Array.isArray(profile?.social_links) && profile.social_links.length > 0) {
    return profile.social_links
      .filter((l) => l && l.url)
      .slice(0, 4)
      .map((l) => ({ title: l.title || l.url, url: withHttp(l.url) }));
  }
  return LEGACY_SOCIAL_FIELDS
    .filter((f) => profile?.[f.key])
    .map((f) => ({ title: f.title, url: withHttp(profile[f.key]) }));
}

const ORG_SUBTYPE_MAP = {
  nonprofit: "Nonprofit", business: "Business", community: "Community Group",
  campaign: "Political Campaign", union: "Labor Union",
  advocacy: "Advocacy Organization", other: "Organization",
};

const RELATIONSHIP_MAP = {
  single: "Single", in_relationship: "In a Relationship", married: "Married",
  domestic_partnership: "Domestic Partnership", divorced: "Divorced", widowed: "Widowed",
};

const TIER_COLORS = { gold: "#9B7A1C", silver: "#7E8DA0", bronze: "#8E5625" };

export default function UserProfilePage() {
  const { userId: profileKey } = useParams();
  const navigate = useNavigate();
  const { user, userId: currentUserId, verifiedUser } = useUser() || {};
  const isLoggedIn = !!user;
  const isVerified = typeof verifiedUser === "boolean" ? verifiedUser : false;

  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("posts");
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
  const [showPublicPrompt, setShowPublicPrompt] = useState(false);
  const [linksModalOpen, setLinksModalOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const loadedTabsRef = useRef(new Set());

  const userId = profile?.id || null;
  const isOwnProfile = Boolean(userId && currentUserId && String(userId) === String(currentUserId));

  const accountType = profile?.account_type?.name || profile?.account_type_name || "citizen";
  const canHostEvents = EVENT_HOSTING_ACCOUNT_TYPES.includes(accountType);
  const isPolitician = isPoliticianAccount(accountType);
  const isOrg = ["organization", "non_profit"].includes(accountType);

  const tabs = useMemo(() => {
    const allTabs = canHostEvents
      ? [{ id: "posts", label: "Posts" }, { id: "events", label: "Events" }, { id: "badges", label: "Badges" }]
      : [{ id: "posts", label: "Posts" }, { id: "badges", label: "Badges" }];
    if (!isLoggedIn) {
      return allTabs.filter((tab) => tab.id === "events");
    }
    return allTabs;
  }, [canHostEvents, isLoggedIn]);

  const loadProfile = useCallback(async () => {
    if (!profileKey) { setError("User ID is required"); setLoading(false); return null; }
    try {
      const data = await getUserProfile(profileKey);
      setProfile(data);
      setIsFollowing(data.is_following || false);
      setIsBlocked(data.viewer_has_blocked || false);
      setError(null);
      return data;
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("This profile is private.");
      } else {
        setError("Failed to load profile");
      }
      setProfile(null);
      return null;
    }
  }, [profileKey]);

  const loadPosts = useCallback(async (resolvedId) => {
    const id = resolvedId || userId;
    if (!id) return;
    setPostsLoading(true);
    try {
      const data = await fetchPosts({ user_id: id, page: 1, page_size: 15 });
      const fetched = data.results || [];
      setPosts(fetched.filter((p) => String(p.user?.id || p.user_id) === String(id)));
    } catch {
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, [userId]);

  const loadEvents = useCallback(async (resolvedId) => {
    const id = resolvedId || userId;
    if (!id) return;
    setEventsLoading(true);
    try {
      const data = await fetchUserEvents(id);
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [userId]);

  const loadBadgesWithLoading = useCallback(async (resolvedId) => {
    const id = resolvedId || userId;
    if (!id) return;
    setBadgesLoading(true);
    try {
      const data = await fetchUserBadgesByUserId(id);
      const filtered = (Array.isArray(data) ? data : []).filter(
        (b) => b.earned && b.series !== "letter_writer"
      );
      setBadges(getHighestEarnedBadges(filtered));
    } catch {
      setBadges([]);
    } finally {
      setBadgesLoading(false);
    }
  }, [userId]);

  const loadInitialProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProfile(null);
    loadedTabsRef.current = new Set();
    setPosts([]);
    setBadges([]);
    setEvents([]);
    const profileData = await loadProfile();
    if (!profileData) {
      setLoading(false);
      return;
    }
    if (!isLoggedIn && EVENT_HOSTING_ACCOUNT_TYPES.includes(
      profileData.account_type?.name || profileData.account_type_name || "citizen"
    )) {
      setActiveTab("events");
    }
    setLoading(false);
  }, [loadProfile, isLoggedIn]);

  const loadTabIfNeeded = useCallback(
    async (tab) => {
      if (!userId || !profile || loadedTabsRef.current.has(tab)) return;
      loadedTabsRef.current.add(tab);
      if (tab === "posts" && isLoggedIn) {
        await loadPosts();
      } else if (tab === "badges" && isLoggedIn) {
        await loadBadgesWithLoading();
      } else if (tab === "events" && canHostEvents) {
        await loadEvents();
      }
    },
    [userId, profile, isLoggedIn, canHostEvents, loadPosts, loadBadgesWithLoading, loadEvents]
  );

  useEffect(() => { loadInitialProfile(); }, [loadInitialProfile]);

  useEffect(() => {
    if (!profile || loading) return;
    loadTabIfNeeded(activeTab);
  }, [activeTab, profile, loading, loadTabIfNeeded]);

  useEffect(() => {
    if (!isLoggedIn) setShowPublicPrompt(true);
  }, [isLoggedIn]);

  const handleFollowToggle = async () => {
    if (!isLoggedIn || isOwnProfile || followLoading) return;
    setFollowLoading(true);
    const prev = isFollowing;
    setIsFollowing(!prev);
    try {
      if (prev) await unfollowUser(userId);
      else await followUser(userId);
      await loadProfile();
    } catch (err) {
      setIsFollowing(prev);
      showToast(getFollowBlockedErrorMessage(err), "error");
    } finally {
      setFollowLoading(false);
    }
  };

  const getDisplayName = () => {
    if (!profile) return "User";
    if (profile.entity_name) return profile.entity_name;
    if (profile.first_name && profile.last_name) return `${profile.first_name} ${profile.last_name}`;
    return profile.username || "User";
  };

  const handleBlockConfirm = async () => {
    setBlockLoading(true);
    try {
      await blockUser(userId);
      setIsBlocked(true);
      setIsFollowing(false);
      setPosts([]);
      setShowBlockConfirm(false);
      showToast(`${getDisplayName()} has been blocked`);
    } catch (err) {
      showToast(err?.message || "Couldn't block this account. Please try again.", "error");
    } finally {
      setBlockLoading(false);
    }
  };

  const handleUnblock = async () => {
    setBlockLoading(true);
    try {
      await unblockUser(userId);
      setIsBlocked(false);
      showToast(`${getDisplayName()} has been unblocked`);
      loadedTabsRef.current.delete("posts");
      await loadPosts();
    } catch (err) {
      showToast(err?.message || "Couldn't unblock this account. Please try again.", "error");
    } finally {
      setBlockLoading(false);
    }
  };

  const profileUrl = buildProfileShareUrl({
    username: profile?.username,
    userId,
  });

  const handleShareProfile = () => setShareOpen(true);

  const getInitials = (name) => name ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "U";

  const getDistrictLabel = () => {
    if (!profile) return null;
    const showState = isOwnProfile || profile.is_state_visible !== false;
    const showDistrict = isOwnProfile || profile.is_district_visible !== false;
    const st = showState ? profile.state : null;
    const dist = showDistrict && profile.district_number && profile.district_number !== "N/A" ? profile.district_number : null;
    if (st && dist) return `${st}-${dist}`;
    return st || null;
  };

  const getPartyLabel = () => {
    if (!profile?.political_party || profile.political_party === "prefer_not_to_say") return null;
    if (profile.political_party === "other" && profile.political_party_other?.trim()) return profile.political_party_other.trim();
    return PARTY_MAP[profile.political_party] || profile.political_party;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress sx={{ color: theme.primary }} />
      </Box>
    );
  }

  if (error || !profile) {
    const isPrivate = error === "This profile is private.";
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc", p: 4 }}>
        <Typography variant="h6" sx={{ color: "#0f172a", mb: 1, textAlign: "center" }}>{error || "User not found."}</Typography>
        {isPrivate && !isLoggedIn && (
          <Typography sx={{ color: "#334155", mb: 2, textAlign: "center", maxWidth: 420 }}>
            Sign in to view this profile, or ask the owner to enable a public profile link.
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
          {!isLoggedIn && (
            <Button component={Link} to="/auth/signup" variant="contained" sx={{ bgcolor: theme.primary, textTransform: "none", fontWeight: 700 }}>
              Join CivixThread
            </Button>
          )}
          <Button variant={isLoggedIn ? "contained" : "outlined"} onClick={() => navigate("/discover")} sx={{ textTransform: "none", fontWeight: 700, ...(isLoggedIn ? { bgcolor: theme.primary } : { borderColor: "#e2e8f0", color: "#0f172a" }) }}>
            Back to Discover
          </Button>
        </Box>
      </Box>
    );
  }

  const displayName = getDisplayName();
  const districtLabel = getDistrictLabel();
  const partyLabel = isPolitician ? getPartyLabel() : null;
  const profileSocialLinks = getProfileSocialLinks(profile);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <AuthRequiredModal
          open={showPublicPrompt}
          onClose={() => setShowPublicPrompt(false)}
          onLogin={() => navigate("/auth/signup")}
          title="You're viewing a public profile"
          description="Sign up to follow this person, join the conversation, and engage with your community."
          confirmLabel="Sign up"
          dismissLabel="Keep browsing"
        />

        <ProfileLinksModal
          open={linksModalOpen}
          onClose={() => setLinksModalOpen(false)}
          links={profileSocialLinks}
          displayName={displayName}
        />

        <ShareProfileModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          profileUrl={profileUrl}
          displayName={displayName}
          title={isOwnProfile ? "Share your profile" : `Share ${displayName}`}
          onCopied={() => showToast("Profile link copied")}
          onCopyFailed={() => showToast("Couldn't copy the link. Please try again.", "error")}
        />

        <DeleteConfirmModal
          open={showBlockConfirm}
          onClose={() => setShowBlockConfirm(false)}
          onConfirm={handleBlockConfirm}
          title={`Block ${displayName}?`}
          description={BLOCK_CONFIRM_DESCRIPTION}
          confirmLabel="Block"
          loading={blockLoading}
        />

        <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
          {/* Profile header */}
          <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
              <Avatar
                src={profile.profile_image || undefined}
                sx={{ width: { xs: 72, sm: 80 }, height: { xs: 72, sm: 80 }, bgcolor: `${theme.primary}20`, color: theme.primary, fontWeight: 700, fontSize: 28 }}
              >
                {getInitials(displayName)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, sm: 22 }, color: "#0f172a", lineHeight: 1.2 }}>{displayName}</Typography>
                      {profile.is_verified && (
                        <VerifiedIcon
                          sx={{
                            fontSize: 18,
                            color: "#8B6914",
                            ml: 1,
                            mb: "3px",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                    {profile.username && (
                      <Typography sx={{ fontSize: 14, color: "#64748b", mb: 1.25 }}>@{profile.username}</Typography>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, flexShrink: 0, alignItems: "center" }}>
                    <IconButton
                      onClick={handleShareProfile}
                      aria-label="Share profile"
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        color: "#0f172a",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                      }}
                    >
                      <FiSend size={18} />
                    </IconButton>
                    {isOwnProfile && (
                      <Button
                        component={Link}
                        to="/account-details"
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#0f172a", borderRadius: "10px" }}
                      >
                        Edit profile
                      </Button>
                    )}
                    {!isOwnProfile && isLoggedIn && !isBlocked && (
                      <Button
                        variant={isFollowing ? "outlined" : "contained"}
                        size="small"
                        onClick={handleFollowToggle}
                        disabled={followLoading}
                        sx={{
                          textTransform: "none", fontWeight: 700, borderRadius: "10px", minWidth: 96,
                          ...(isFollowing
                            ? { borderColor: "#e2e8f0", color: "#0f172a", bgcolor: "#fff" }
                            : { bgcolor: theme.primary, color: "#fff", "&:hover": { bgcolor: theme.primaryHover } }),
                        }}
                      >
                        {followLoading ? <CircularProgress size={18} color="inherit" /> : isFollowing ? "Following" : "Follow"}
                      </Button>
                    )}
                    {!isOwnProfile && isLoggedIn && (
                      <>
                        <IconButton
                          onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
                          aria-label="More profile actions"
                          size="small"
                          sx={{
                            width: 40,
                            height: 40,
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            color: "#0f172a",
                            bgcolor: "#fff",
                            "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Menu
                          anchorEl={profileMenuAnchor}
                          open={Boolean(profileMenuAnchor)}
                          onClose={() => setProfileMenuAnchor(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                          PaperProps={{ sx: { borderRadius: "12px" } }}
                        >
                          <MenuItem
                            onClick={() => {
                              setProfileMenuAnchor(null);
                              if (isBlocked) handleUnblock();
                              else setShowBlockConfirm(true);
                            }}
                            disabled={blockLoading}
                            sx={{ gap: 1.5, py: 1.25, color: isBlocked ? "#0f172a" : "#ef4444" }}
                          >
                            <BlockIcon fontSize="small" sx={{ color: isBlocked ? "#0f172a" : "#ef4444" }} />
                            {isBlocked ? "Unblock user" : "Block user"}
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </Box>
                </Box>

                {isBlocked && (
                  <Box
                    sx={{
                      mb: 1.5,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: "10px",
                      bgcolor: "#fef2f2",
                      border: "1px solid #fecaca",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                      You blocked this account. Their posts and comments stay hidden from you until you unblock them.
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 1.25 }}>
                  {[
                    { value: posts.length, label: "Posts" },
                    { value: profile.followers_count || 0, label: "Followers" },
                    { value: profile.following_count || 0, label: "Following" },
                  ].map((stat) => (
                    <Typography key={stat.label} sx={{ fontSize: 14, color: "#0f172a" }}>
                      <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>{stat.value}</Box>
                      <Box component="span" sx={{ color: "#64748b", fontWeight: 500 }}>{stat.label}</Box>
                    </Typography>
                  ))}
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {isOrg && profile.organization_subtype && (
                  <Chip icon={<BusinessIcon sx={{ fontSize: 14 }} />} label={ORG_SUBTYPE_MAP[profile.organization_subtype] || profile.organization_subtype}
                    size="small" sx={{ height: 24, fontSize: 12, bgcolor: `${theme.primary}15`, color: theme.primary, "& .MuiChip-icon": { color: theme.primary } }} />
                )}
                {isOrg && !profile.is_verified && (
                  <Chip icon={<ShieldOutlinedIcon sx={{ fontSize: 14 }} />} label="Unverified" size="small"
                    sx={{ height: 24, fontSize: 12, bgcolor: "#f1f5f9", color: "#0f172a" }} />
                )}
                {!isOrg && districtLabel && (
                  <Chip label={districtLabel} size="small"
                    sx={{ height: 24, fontSize: 12, bgcolor: "#f1f5f9", color: "#0f172a", fontWeight: 600 }} />
                )}
                {partyLabel && (
                  <Chip label={partyLabel} size="small"
                    sx={{ height: 24, fontSize: 12, bgcolor: "#f1f5f9", color: "#0f172a", fontWeight: 600 }} />
                )}
                {!isOrg && profile.is_open_to_volunteering && (
                  <Chip icon={<VolunteerActivismOutlinedIcon sx={{ fontSize: 14 }} />} label="Open to volunteering" size="small"
                    sx={{ height: 24, fontSize: 12, bgcolor: "#ecfdf5", color: "#065f46" }} />
                )}
                {!isOrg && profile.show_relationship_status && profile.relationship_status && profile.relationship_status !== "prefer_not_to_say" && (
                  <Chip label={RELATIONSHIP_MAP[profile.relationship_status] || profile.relationship_status} size="small"
                    sx={{ height: 24, fontSize: 12, bgcolor: "#f1f5f9", color: "#0f172a", fontWeight: 600 }} />
                )}
              </Box>
            </Box>
          </Box>

          {profile.bio && (
            <Typography sx={{ fontSize: 15, color: "#334155", mt: 2, lineHeight: 1.6 }}>{profile.bio}</Typography>
          )}

          {/* Org mission */}
          {isOrg && profile.organization_mission && profile.organization_mission !== profile.bio && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", mb: 0.5 }}>Mission</Typography>
              <Typography sx={{ fontSize: 15, color: "#334155", lineHeight: 1.6 }}>{profile.organization_mission}</Typography>
            </Box>
          )}

          {/* Politician current office */}
          {isPolitician && profile.current_office && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>Elected Office:</Typography>
              <Typography sx={{ fontSize: 14, color: "#0f172a" }}>{profile.current_office}</Typography>
            </Box>
          )}

          {/* Politician website */}
          {isPolitician && profile.campaign_website && (
            <Box
              component="a"
              href={profile.campaign_website.startsWith("http") ? profile.campaign_website : `https://${profile.campaign_website}`}
              target="_blank" rel="noopener noreferrer"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 1, fontSize: 14, color: theme.primary, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              <LanguageIcon sx={{ fontSize: 16 }} /> Website <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Box>
          )}

          {/* Named links (all account types) - single entry point opens a modal */}
          {profileSocialLinks.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                onClick={() => setLinksModalOpen(true)}
                variant="outlined"
                startIcon={<LinkIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#0f172a",
                  borderColor: "#e2e8f0",
                  borderRadius: "9999px",
                  bgcolor: "#fff",
                  "&:hover": { borderColor: theme.primary, color: theme.primary, bgcolor: "#f8fafc" },
                }}
              >
                Links · {profileSocialLinks.length}
              </Button>
            </Box>
          )}

          {/* Org contact email */}
          {isOrg && profile.organization_contact_email && (
            <Box
              component="a"
              href={`mailto:${profile.organization_contact_email}`}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 1, fontSize: 14, color: "#334155", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              <EmailOutlinedIcon sx={{ fontSize: 16 }} /> {profile.organization_contact_email}
            </Box>
          )}
          </Box>

          {tabs.length > 0 && (
            <Box
              role="tablist"
              aria-label="Profile sections"
              sx={{
                display: "flex",
                borderTop: "1px solid #e5e7eb",
                px: { xs: 1, sm: 2 },
              }}
            >
              {tabs.map((tab) => {
                const selected = activeTab === tab.id;
                return (
                  <Button
                    key={tab.id}
                    role="tab"
                    aria-selected={selected}
                    onClick={() => setActiveTab(tab.id)}
                    sx={{
                      flex: 1,
                      minWidth: 88,
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 0,
                      textTransform: "none",
                      fontWeight: selected ? 700 : 600,
                      fontSize: 15,
                      color: selected ? theme.primary : "#64748b",
                      bgcolor: "transparent",
                      borderBottom: selected ? `2px solid ${theme.primary}` : "2px solid transparent",
                      mb: "-1px",
                      "&:hover": { bgcolor: "#f8fafc", color: selected ? theme.primary : "#0f172a" },
                    }}
                  >
                    {tab.label}
                  </Button>
                );
              })}
            </Box>
          )}

          <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: { xs: 220, sm: 260 } }}>
        {isLoggedIn && activeTab === "posts" && (
          <Box>
            {postsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={32} sx={{ color: theme.primary }} />
              </Box>
            ) : posts.length === 0 ? (
              <EmptyState
                icon={<ArticleOutlinedIcon sx={{ fontSize: 40, color: theme.primary }} />}
                title={isBlocked ? "Posts hidden" : "No posts yet"}
                subtitle={
                  isBlocked
                    ? "You blocked this account, so their posts are hidden. Unblock them to see this tab again."
                    : isOwnProfile
                      ? "Share an action you took, a question you're weighing, or an idea worth discussing."
                      : "This user hasn't shared any posts yet."
                }
              />
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={{ ...post, has_upvoted: post.has_upvoted ?? post.viewer_has_upvoted, has_followed: post.has_followed ?? post.viewer_is_following }}
                  currentUserId={currentUserId}
                  isVerified={isVerified}
                  onUpvote={() => {}}
                  onFollow={() => {}}
                  onAuthRequired={() => {}}
                  onEdit={() => {}}
                  onDelete={() => {}}
                  onReport={() => {}}
                  onActionCountChange={(postToUpdate, { logged, actionCount, verifiedActionCount, delta }) => {
                    setPosts((prevPosts) =>
                      prevPosts.map((p) =>
                        p.id === postToUpdate.id
                          ? {
                              ...p,
                              action_count:
                                typeof actionCount === "number"
                                  ? actionCount
                                  : Math.max(0, (p.action_count || 0) + delta),
                              verified_action_count:
                                typeof verifiedActionCount === "number"
                                  ? verifiedActionCount
                                  : isVerified
                                    ? Math.max(0, (p.verified_action_count ?? 0) + delta)
                                    : p.verified_action_count,
                              viewer_has_logged_action: logged,
                            }
                          : p
                      )
                    );
                  }}
                />
              ))
            )}
          </Box>
        )}

        {activeTab === "events" && canHostEvents && (
          <Box>
            {eventsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={32} sx={{ color: theme.primary }} />
              </Box>
            ) : (
              <>
            {events.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button
                  component={Link}
                  to={`/events?organizer=${userId}`}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 700, borderColor: "#e2e8f0", color: theme.primary, borderRadius: "10px" }}
                >
                  View all events
                </Button>
              </Box>
            )}
            {events.length === 0 ? (
              <EmptyState
                icon={<EventOutlinedIcon sx={{ fontSize: 40, color: theme.primary }} />}
                title="No events yet"
                subtitle={isOwnProfile ? "Host an event to connect with your community." : "No events hosted yet."}
              />
            ) : (
              events.map((event) => (
                <Box
                  key={event.id}
                  onClick={() => navigate(`/events/${event.id}`)}
                  sx={{
                    display: "flex", gap: 2, p: 2, mb: 1.5, bgcolor: "#fff", borderRadius: "12px",
                    border: "1px solid #e5e7eb", cursor: "pointer", transition: "background 0.15s",
                    "&:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a", mb: 0.5 }}>{event.title}</Typography>
                    {event.start_datetime && (
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                        {new Date(event.start_datetime).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                      {event.is_virtual
                        ? "Virtual Event"
                        : [event.location_name, event.city, event.state].filter(Boolean).join(", ") || ""}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
              </>
            )}
          </Box>
        )}

        {activeTab === "badges" && isLoggedIn && (
          <Box>
            {badgesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={32} sx={{ color: theme.primary }} />
              </Box>
            ) : badges.length === 0 ? (
              <EmptyState
                icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 40, color: theme.primary }} />}
                title="No badges yet"
                subtitle={isOwnProfile ? "Badges recognize civic action. Comment on bills, contact your reps, attend events, and volunteer in your community." : "No badges earned yet."}
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {badges.map((badge) => {
                  const tierMatch = badge.name?.match(/\((Gold|Silver|Bronze)\)/i);
                  const tier = tierMatch ? tierMatch[1].toLowerCase() : null;
                  const tierColor = TIER_COLORS[tier] || theme.primary;
                  const cleanName = badge.name?.replace(/\s*\((Gold|Silver|Bronze)\)/i, "") || badge.name;

                  return (
                    <Box key={badge.id} sx={{ display: "flex", gap: 2, p: 2, bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", alignItems: "center" }}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center",
                        border: `2px solid ${tier ? tierColor : "#e2e8f0"}`, bgcolor: `${tierColor}10`,
                      }}>
                        <EmojiEventsOutlinedIcon sx={{ fontSize: 24, color: tierColor }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{cleanName}</Typography>
                          {tier && (
                            <Chip label={tier.toUpperCase()} size="small"
                              sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: `${tierColor}15`, color: tierColor }} />
                          )}
                        </Box>
                        {badge.description && (
                          <Typography sx={{ fontSize: 13, color: "#0f172a", mt: 0.25 }}>{badge.description}</Typography>
                        )}
                        {getBadgeCountLabel(badge.series, badge.current) && (
                          <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600, mt: 0.25 }}>
                            {getBadgeCountLabel(badge.series, badge.current)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%", fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function EmptyState({ icon, title, subtitle, iconBg = "#ecfdf5", action = null }) {
  return (
    <Box sx={{ textAlign: "center", py: { xs: 4, sm: 5 }, px: 2 }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 72, height: 72, borderRadius: "16px", bgcolor: iconBg, mb: 2 }}>
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#0f172a", mb: 1 }}>{title}</Typography>
      <Typography sx={{ fontSize: 15, color: "#64748b", lineHeight: 1.5, maxWidth: 360, mx: "auto" }}>{subtitle}</Typography>
      {action}
    </Box>
  );
}
